import * as React from "react"
import { Header, fadeAway, type HeaderApi } from "./Header"
import { Menu } from "./Menu"
import { WorkChrome } from "./WorkChrome"
import { ContactOverlay } from "./Contact"
import { useShell, type Tone } from "./context"
import { MENU, CHROME, WORK_URL } from "./settings"
import "./shell.css"

export type ShellApi = {
    headerMix: (to: Tone, d: number) => void
    headerTone: (tone: Tone, fade: boolean) => void
    chromeTone: (tone: Tone) => void
    leaveForHome: () => void
    /** off a work page to a page without the work navigation: it fades out */
    leaveWork: () => void
    prepareHomeIntro: () => void
}

type Props = {
    /** the page on screen */
    kind: "home" | "work" | "about"
    page: string
    /** work page category ("Sports") */
    category: string
    tone: Tone
    first: { kind: "home" | "work" | "about"; tone: Tone }
}

/** Menu size, the same on every page: designed at 1680x1080, scaled down
 *  proportionally on smaller screens. */
function useMenuScale() {
    const get = () => Math.min(1, innerWidth / 1680, innerHeight / 1080)
    const [s, setS] = React.useState(get)
    React.useEffect(() => {
        const on = () => setS(get())
        addEventListener("resize", on)
        return () => removeEventListener("resize", on)
    }, [])
    return s
}

/** Logo, local time, menu and (on work pages) the section indicator and
 *  category tab. Mounted once; pages change underneath it. */
export const Shell = React.forwardRef<ShellApi, Props>(function Shell({ kind, page, category, tone, first }, api) {
    const shell = useShell()!
    const header = React.useRef<HeaderApi>(null)
    const root = React.useRef<HTMLDivElement>(null)
    const [chromeTone, setChromeTone] = React.useState<Tone>(tone)
    const menuScale = useMenuScale()
    // work chrome follows the page, except while a category switch recolours it early
    React.useEffect(() => {
        if (kind === "work") setChromeTone(tone)
    }, [kind, page])

    React.useImperativeHandle(api, () => ({
        headerMix: (to, d) => header.current?.mix(to, d),
        headerTone: (t, fade) => header.current?.tone(t, fade),
        chromeTone: (t) => setChromeTone(t),
        leaveForHome() {
            header.current?.fadeOut()
            const r = root.current
            if (!r) return
            r.querySelectorAll<HTMLElement>(".work-sections, .work-category, .n27-wrap").forEach((el) => fadeAway(el))
        },
        leaveWork() {
            const r = root.current
            if (!r) return
            r.querySelectorAll<HTMLElement>(".work-sections, .work-category").forEach((el) => fadeAway(el))
        },
        prepareHomeIntro() {
            header.current?.prepareIntro()
            const wrap = root.current?.querySelector<HTMLElement>(".n27-wrap")
            if (wrap) {
                wrap.style.transition = "none"
                wrap.style.opacity = ""
            }
            const n = shell.intro.current
            if (n.nav) n.nav.style.opacity = "0"
            if (n.navIcon) n.navIcon.style.opacity = "0"
        },
    }))

    const menu = MENU
    const homeFirst = first.kind === "home"
    // the home intro animates the menu button in: it starts hidden there
    const hidden = kind === "home" ? { opacity: 0 } : undefined
    const onHome = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
        e.preventDefault()
        if (kind !== "home") shell.navigate("/")
    }
    return (
        <div ref={root} className="sh" data-kind={kind}>
            <Header
                ref={header}
                initialTone={first.tone}
                introFirst={homeFirst}
                enter={!homeFirst}
                mobile={shell.mobile}
                homeHref="/"
                onHome={onHome}
                intro={shell.intro}
            />
            {kind === "work" && (
                <WorkChrome
                    {...CHROME}
                    categories={MENU.categories}
                    currentCategory={category}
                    workUrl={WORK_URL}
                    theme={chromeTone as any}
                    mobile={shell.mobile}
                    onNavigate={shell.navigate}
                />
            )}
            {/* a fresh menu on the home and on the work pages (as before: one per
                page), so the home intro never starts with the menu's shape still up */}
            <Menu
                key={kind}
                menuOpen={shell.menuOpen}
                onOpen={shell.setMenuOpen}
                mobile={shell.mobile}
                menuSpeed={2}
                menuFont={menu.menuFont}
                submenuFont={menu.submenuFont}
                categories={menu.categories}
                currentCategory={category}
                workUrl={WORK_URL}
                feedUrl="/feed"
                contactUrl="/about"
                scale={menuScale}
                introRefs={{
                    button: (el: HTMLElement | null) => el && (shell.intro.current.nav = el),
                    icon: (el: HTMLElement | null) => el && (shell.intro.current.navIcon = el),
                }}
                introStyle={hidden}
            />
            <ContactOverlay />
        </div>
    )
})
